// Sports / disciplines list for Sport Fund profiles.
// File path is kept as `denominations.ts` for backward-compatible aliases (see `denominations` export below).

export const sports = [
    // Athletics & Endurance
    { value: 'athletics_track', label: 'Athletics — Track' },
    { value: 'athletics_field', label: 'Athletics — Field' },
    { value: 'marathon_running', label: 'Marathon / Road Running' },
    { value: 'trail_running', label: 'Trail Running' },
    { value: 'triathlon', label: 'Triathlon' },
    { value: 'cycling_road', label: 'Cycling — Road' },
    { value: 'cycling_track', label: 'Cycling — Track' },
    { value: 'mountain_biking', label: 'Mountain Biking' },
    { value: 'swimming', label: 'Swimming' },
    { value: 'rowing', label: 'Rowing' },
    { value: 'canoe_kayak', label: 'Canoe / Kayak' },

    // Team Sports
    { value: 'football', label: 'Football (Soccer)' },
    { value: 'rugby', label: 'Rugby' },
    { value: 'basketball', label: 'Basketball' },
    { value: 'netball', label: 'Netball' },
    { value: 'volleyball', label: 'Volleyball' },
    { value: 'hockey', label: 'Field Hockey' },
    { value: 'ice_hockey', label: 'Ice Hockey' },
    { value: 'cricket', label: 'Cricket' },
    { value: 'baseball', label: 'Baseball' },
    { value: 'american_football', label: 'American Football' },
    { value: 'handball', label: 'Handball' },
    { value: 'water_polo', label: 'Water Polo' },

    // Racket Sports
    { value: 'tennis', label: 'Tennis' },
    { value: 'badminton', label: 'Badminton' },
    { value: 'table_tennis', label: 'Table Tennis' },
    { value: 'squash', label: 'Squash' },
    { value: 'padel', label: 'Padel' },

    // Combat Sports
    { value: 'boxing', label: 'Boxing' },
    { value: 'judo', label: 'Judo' },
    { value: 'karate', label: 'Karate' },
    { value: 'taekwondo', label: 'Taekwondo' },
    { value: 'wrestling', label: 'Wrestling' },
    { value: 'mma', label: 'Mixed Martial Arts (MMA)' },
    { value: 'fencing', label: 'Fencing' },

    // Strength & Gymnastics
    { value: 'weightlifting', label: 'Weightlifting' },
    { value: 'powerlifting', label: 'Powerlifting' },
    { value: 'crossfit', label: 'CrossFit' },
    { value: 'gymnastics', label: 'Gymnastics' },
    { value: 'climbing', label: 'Sport Climbing' },

    // Winter Sports
    { value: 'alpine_skiing', label: 'Alpine Skiing' },
    { value: 'cross_country_skiing', label: 'Cross-Country Skiing' },
    { value: 'snowboarding', label: 'Snowboarding' },
    { value: 'figure_skating', label: 'Figure Skating' },
    { value: 'speed_skating', label: 'Speed Skating' },

    // Racket/Target & Precision
    { value: 'golf', label: 'Golf' },
    { value: 'archery', label: 'Archery' },
    { value: 'shooting', label: 'Shooting' },
    { value: 'equestrian', label: 'Equestrian' },

    // Water & Board Sports
    { value: 'surfing', label: 'Surfing' },
    { value: 'sailing', label: 'Sailing' },
    { value: 'diving', label: 'Diving' },
    { value: 'skateboarding', label: 'Skateboarding' },

    // Other
    { value: 'esports', label: 'Esports' },
    { value: 'dance_sport', label: 'Dance Sport' },
    { value: 'para_sport', label: 'Para Sport' },
    { value: 'other', label: 'Other' },
    { value: 'undecided', label: 'Not specified' },
];

export const sportCategories: Record<string, string[]> = {
    'Athletics & Endurance': ['athletics_track', 'athletics_field', 'marathon_running', 'trail_running', 'triathlon', 'cycling_road', 'cycling_track', 'mountain_biking', 'swimming', 'rowing', 'canoe_kayak'],
    'Team Sports': ['football', 'rugby', 'basketball', 'netball', 'volleyball', 'hockey', 'ice_hockey', 'cricket', 'baseball', 'american_football', 'handball', 'water_polo'],
    'Racket Sports': ['tennis', 'badminton', 'table_tennis', 'squash', 'padel'],
    'Combat Sports': ['boxing', 'judo', 'karate', 'taekwondo', 'wrestling', 'mma', 'fencing'],
    'Strength & Gymnastics': ['weightlifting', 'powerlifting', 'crossfit', 'gymnastics', 'climbing'],
    'Winter Sports': ['alpine_skiing', 'cross_country_skiing', 'snowboarding', 'figure_skating', 'speed_skating'],
    'Target & Precision': ['golf', 'archery', 'shooting', 'equestrian'],
    'Water & Board Sports': ['surfing', 'sailing', 'diving', 'skateboarding'],
    'Other': ['esports', 'dance_sport', 'para_sport', 'other', 'undecided'],
};

/**
 * Looks up the display label for a stored sport value. Falls back to a
 * humanized version of the raw value instead of the literal internal key (a
 * renamed/removed catalog entry) — ported from mystudyfund's equivalent
 * fieldOfStudyDisplayLabel fix (same Record<string,string>-with-raw-fallback
 * shape, same risk: any signed-in user can read another creator's profile).
 */
export function sportDisplayLabel(value?: string | null): string | null {
    if (!value) return null;
    const match = sports.find(d => d.value === value)?.label;
    if (match) return match;
    if (!value.includes('_')) return value;
    return value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// --- Backward-compatible aliases (kept so existing imports keep working) ---
/** @deprecated Use `sports` instead */
export const fieldsOfStudy = sports;
/** @deprecated Use `sportCategories` instead */
export const fieldOfStudyCategories = sportCategories;
/** @deprecated Use `sports` instead */
export const denominations = sports;
/** @deprecated Use `sportCategories` instead */
export const denominationCategories = sportCategories;
